import React, { useEffect, useMemo, useRef } from 'react';
import { Font, FontVariant } from 'Vendor/Utils/StudioTypes';

interface FontFamilyDropdownProps {
  value: string;
  onChange: (family: string) => void;
  onVariantChange?: (family: string, variant: FontVariant) => void;
  platformFonts?: Font[];
  label?: string;
  showVariants?: boolean;
  selectedVariantName?: string;
}

const FontFamilyDropdown: React.FC<FontFamilyDropdownProps> = ({
  value,
  onChange,
  onVariantChange,
  platformFonts = [],
  label,
  showVariants = false,
  selectedVariantName,
}) => {
  const families = useMemo(() => {
    return platformFonts.map((f) => f.family).sort();
  }, [platformFonts]);

  const sortedFonts = useMemo(() => {
    return [...platformFonts].sort((a, b) => a.family.localeCompare(b.family));
  }, [platformFonts]);

  const hasInitialized = useRef(false);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  useEffect(() => {
    if (!value && families.length > 0 && families[0] && !hasInitialized.current) {
      hasInitialized.current = true;
      onChangeRef.current(families[0]);
    }
  // Only re-run if value or families change — NOT when onChange changes reference
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, families]);

  const displayText = useMemo(() => {
    if (showVariants && value && selectedVariantName) {
      return `${value} — ${selectedVariantName}`;
    }
    return value || families[0] || 'Select font';
  }, [showVariants, value, selectedVariantName, families]);

  const handleVariantClick = (font: Font, variant: FontVariant) => {
    onChange(font.family);
    onVariantChange?.(font.family, variant);
  };

  return (
    <div className="text-block-dropdown">
      {label && <label className="form-label fs-5 text-white">{label}</label>}
      <button
        className="btn btn-secondary dropdown-toggle w-100 text-start"
        type="button"
        data-bs-toggle="dropdown"
        aria-expanded="false"
      >
        {displayText}
      </button>
      <ul
        className="dropdown-menu w-100"
        style={showVariants ? { maxHeight: '300px', overflowY: 'auto' } : undefined}
      >
        {showVariants
          ? sortedFonts.map((font, index) => (
              <React.Fragment key={font.family}>
                <li>
                  <h6 className="dropdown-header text-uppercase fw-bold">
                    {font.family}
                  </h6>
                </li>
                {font.variants.map((variant) => (
                  <li key={variant.file}>
                    <button
                      className={`dropdown-item ps-4 ${
                        value === font.family && selectedVariantName === variant.name
                          ? 'active'
                          : ''
                      }`}
                      type="button"
                      onClick={() => handleVariantClick(font, variant)}
                    >
                      {variant.name || `Weight ${variant.weight}`}
                    </button>
                  </li>
                ))}
                {index < sortedFonts.length - 1 && (
                  <li>
                    <hr className="dropdown-divider" />
                  </li>
                )}
              </React.Fragment>
            ))
          : families.map((family) => (
              <li key={family}>
                <button
                  className="dropdown-item"
                  type="button"
                  onClick={() => onChange(family)}
                >
                  {family}
                </button>
              </li>
            ))}
      </ul>
    </div>
  );
};

export default FontFamilyDropdown;
